<div class="bg-night text-white max-h-[90vh] overflow-hidden overflow-x-hidden flex flex-col">
  <div class="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
    <h2 class="text-xl lg:text-2xl font-bold flex items-center gap-3">
      <span class="material-symbols-outlined text-neon">payments</span>
      {{ 'pos.checkout.title' | translate }}
    </h2>
    <button
      type="button"
      (click)="close()"
      class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">
    <section>
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-medium text-white/60 flex items-center gap-2">
          <span class="material-symbols-outlined text-base">receipt</span>
          {{ 'pos.checkout.cart_summary' | translate }}
        </h3>
        @if (checkoutType() === CheckoutType.WALK_IN && hasItemsWithPointsOption()) {
          <span class="text-xs text-amber-400/70 flex items-center gap-1">
            <span class="material-symbols-outlined text-sm">touch_app</span>
            {{ 'pos.checkout.tap_to_toggle_points' | translate }}
          </span>
        }
      </div>
      <div class="bg-white/5 rounded-xl p-4 space-y-3">
        @for (item of items(); track $index) {
          <div
            class="flex items-center justify-between gap-2"
            data-testid="checkout-item"
            [attr.data-product-name]="item.name">
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <span
                class="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">
                {{ item.qty }}
              </span>
              <div class="flex flex-col min-w-0">
                <span class="text-white truncate">{{ item.name }}</span>
                @if (item.modificationNote) {
                  <span class="text-xs text-purple-400/70 truncate block">{{ item.modificationNote }}</span>
                }
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              @if (checkoutType() === CheckoutType.WALK_IN && item.canPayWithPoints) {
                <button
                  type="button"
                  data-testid="item-payment-toggle"
                  [attr.data-payment-type]="item.paymentType"
                  (click)="toggleItemPaymentType($index)"
                  class="flex items-center gap-1 px-2 py-1 rounded-lg border transition-all text-xs"
                  [class.border-neon]="item.paymentType === PaymentType.Money"
                  [class.text-neon]="item.paymentType === PaymentType.Money"
                  [class.border-amber-500]="item.paymentType === PaymentType.Points"
                  [class.text-amber-400]="item.paymentType === PaymentType.Points"
                  [ngClass]="{
                    'bg-neon/10': item.paymentType === PaymentType.Money,
                    'bg-amber-500/10': item.paymentType === PaymentType.Points,
                  }">
                  @if (item.paymentType === PaymentType.Money) {
                    <span class="material-symbols-outlined text-sm">euro</span>
                    <span>{{ (item.priceCents * item.qty) / 100 | number: '1.2-2' }}</span>
                  } @else {
                    <span class="material-symbols-outlined text-sm">star</span>
                    <span>{{ item.pointsCost * item.qty }} b.</span>
                  }
                </button>
              } @else {
                <span class="font-semibold text-neon">
                  {{ (item.priceCents * item.qty) / 100 | number: '1.2-2' }} €
                </span>
              }
            </div>
          </div>
        }
        <div class="border-t border-white/10 pt-4 mt-4">
          @if (calculatedTotalMoney() > 0) {
            <div class="flex items-center justify-between">
              <span class="text-lg text-white/70">{{ 'pos.checkout.total_money' | translate }}</span>
              <span
                class="text-3xl lg:text-4xl font-black text-neon"
                data-testid="checkout-total-money"
                >{{ calculatedTotalMoney() / 100 | number: '1.2-2' }} €</span
              >
            </div>
          }
          @if (calculatedTotalPoints() > 0) {
            <div class="flex items-center justify-between mt-2">
              <span class="text-white/70">{{ 'pos.checkout.total_points' | translate }}</span>
              <span
                class="text-xl font-bold text-amber-400"
                data-testid="checkout-total-points"
                >{{ calculatedTotalPoints() }} {{ 'pos.checkout.points' | translate }}</span
              >
            </div>
          }
        </div>
      </div>
    </section>

    <section>
      <h3 class="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
        <span class="material-symbols-outlined text-base">account_balance_wallet</span>
        {{ 'pos.checkout.payment_type' | translate }}
      </h3>
      <div class="grid grid-cols-4 gap-2">
        <button
          type="button"
          data-testid="checkout-type-walkin"
          (click)="setCheckoutType(CheckoutType.WALK_IN)"
          class="px-3 py-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1"
          [class.border-neon]="checkoutType() === CheckoutType.WALK_IN"
          [class.bg-neon]="checkoutType() === CheckoutType.WALK_IN"
          [class.text-night]="checkoutType() === CheckoutType.WALK_IN"
          [class.text-white]="checkoutType() !== CheckoutType.WALK_IN"
          [ngClass]="{
            'border-white/20': checkoutType() !== CheckoutType.WALK_IN,
            'bg-white/5': checkoutType() !== CheckoutType.WALK_IN,
          }">
          <span class="material-symbols-outlined text-xl">shopping_bag</span>
          <span class="text-xs font-medium">{{ 'pos.checkout.walk_in' | translate }}</span>
        </button>

        <button
          type="button"
          data-testid="checkout-type-newtab"
          (click)="setCheckoutType(CheckoutType.NEW_TAB)"
          class="px-3 py-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1"
          [class.border-neon]="checkoutType() === CheckoutType.NEW_TAB"
          [class.bg-neon]="checkoutType() === CheckoutType.NEW_TAB"
          [class.text-night]="checkoutType() === CheckoutType.NEW_TAB"
          [class.text-white]="checkoutType() !== CheckoutType.NEW_TAB"
          [ngClass]="{
            'border-white/20': checkoutType() !== CheckoutType.NEW_TAB,
            'bg-white/5': checkoutType() !== CheckoutType.NEW_TAB,
          }">
          <span class="material-symbols-outlined text-xl">person_add</span>
          <span class="text-xs font-medium">{{ 'pos.checkout.new_tab' | translate }}</span>
        </button>

        <button
          type="button"
          data-testid="checkout-type-existingtab"
          (click)="setCheckoutType(CheckoutType.EXISTING_TAB)"
          class="px-3 py-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1"
          [class.border-neon]="checkoutType() === CheckoutType.EXISTING_TAB"
          [class.bg-neon]="checkoutType() === CheckoutType.EXISTING_TAB"
          [class.text-night]="checkoutType() === CheckoutType.EXISTING_TAB"
          [class.text-white]="checkoutType() !== CheckoutType.EXISTING_TAB"
          [ngClass]="{
            'border-white/20': checkoutType() !== CheckoutType.EXISTING_TAB,
            'bg-white/5': checkoutType() !== CheckoutType.EXISTING_TAB,
          }">
          <span class="material-symbols-outlined text-xl">receipt_long</span>
          <span class="text-xs font-medium">{{ 'pos.checkout.existing_tab' | translate }}</span>
        </button>

        <button
          type="button"
          data-testid="checkout-type-overhead"
          (click)="setCheckoutType(CheckoutType.OVERHEAD)"
          class="px-3 py-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1"
          [class.border-red-500]="checkoutType() === CheckoutType.OVERHEAD"
          [class.bg-red-500]="checkoutType() === CheckoutType.OVERHEAD"
          [class.text-white]="checkoutType() === CheckoutType.OVERHEAD"
          [ngClass]="{
            'border-white/20': checkoutType() !== CheckoutType.OVERHEAD,
            'bg-white/5': checkoutType() !== CheckoutType.OVERHEAD,
          }">
          <span class="material-symbols-outlined text-xl">trending_down</span>
          <span class="text-xs font-medium">{{ 'pos.checkout.overhead' | translate }}</span>
        </button>
      </div>
    </section>

    @if (checkoutType() === CheckoutType.WALK_IN) {
      <section>
        <h3 class="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
          <span class="material-symbols-outlined text-base">payment</span>
          {{ 'pos.checkout.payment_method' | translate }}
        </h3>
        <div class="flex gap-3">
          <button
            type="button"
            data-testid="payment-method-cash"
            (click)="paymentMethod.set(PaymentMethod.Cash)"
            class="flex-1 p-4 rounded-xl border-2 transition-all flex items-center justify-center gap-3"
            [class.border-neon]="paymentMethod() === PaymentMethod.Cash"
            [ngClass]="{
              'bg-neon/10': paymentMethod() === PaymentMethod.Cash,
              'border-white/10': paymentMethod() !== PaymentMethod.Cash,
              'bg-white/5': paymentMethod() !== PaymentMethod.Cash,
            }">
            <span
              class="material-symbols-outlined text-2xl"
              [class.text-neon]="paymentMethod() === PaymentMethod.Cash"
              >payments</span
            >
            <span class="font-semibold">{{ 'pos.checkout.cash' | translate }}</span>
          </button>

          <button
            type="button"
            data-testid="payment-method-card"
            (click)="paymentMethod.set(PaymentMethod.Card)"
            class="flex-1 p-4 rounded-xl border-2 transition-all flex items-center justify-center gap-3"
            [class.border-neon]="paymentMethod() === PaymentMethod.Card"
            [ngClass]="{
              'bg-neon/10': paymentMethod() === PaymentMethod.Card,
              'border-white/10': paymentMethod() !== PaymentMethod.Card,
              'bg-white/5': paymentMethod() !== PaymentMethod.Card,
            }">
            <span
              class="material-symbols-outlined text-2xl"
              [class.text-neon]="paymentMethod() === PaymentMethod.Card"
              >credit_card</span
            >
            <span class="font-semibold">{{ 'pos.checkout.card' | translate }}</span>
          </button>
        </div>

        @if (paymentMethod() === PaymentMethod.Card && qrCodeDataUrl()) {
          <div class="mt-4 bg-white/5 rounded-xl p-4">
            <div class="flex flex-col items-center gap-3">
              <p class="text-sm text-white/70 text-center">{{ 'pos.checkout.scan_qr_for_payment' | translate }}</p>
              <div
                class="bg-white rounded-xl p-2"
                data-testid="qr-code-container">
                <img
                  [src]="qrCodeDataUrl()"
                  alt="QR Code"
                  class="w-48 h-48"
                  data-testid="qr-code-image" />
              </div>
              <div class="text-center">
                <p class="text-xs text-white/50 mb-1">{{ 'pos.checkout.iban' | translate }}</p>
                <p class="text-sm font-mono text-white select-all">{{ PAYMENT_IBAN }}</p>
              </div>
              @if (currentVariableSymbol()) {
                <div class="text-center">
                  <p class="text-xs text-white/50 mb-1">{{ 'pos.checkout.variable_symbol' | translate }}</p>
                  <p
                    class="text-sm font-mono text-white select-all"
                    data-testid="variable-symbol"
                    >{{ currentVariableSymbol() }}</p
                  >
                </div>
              }
              <p class="text-lg font-bold text-neon">{{ calculatedTotalMoney() / 100 | number: '1.2-2' }} €</p>
            </div>
          </div>
        }
      </section>

      @if (hasPointsItems()) {
        <section>
          <div class="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-3">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-amber-400 mt-0.5">info</span>
              <p class="text-sm text-white/80">{{ 'pos.checkout.points_user_required' | translate }}</p>
            </div>
          </div>

          <h3 class="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
            <span class="material-symbols-outlined text-base">person_search</span>
            {{ 'pos.checkout.select_points_user' | translate }}
          </h3>

          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-white/30">search</span>
            <input
              type="text"
              [value]="walkInUserSearchQuery()"
              (input)="searchWalkInUsers($any($event.target).value)"
              data-testid="walkin-points-user-search"
              class="w-full bg-transparent border border-white rounded-full py-3 pl-12 pr-4 text-white placeholder-white/70 focus:outline-none focus:border-amber-500 transition-all"
              placeholder="{{ 'pos.checkout.search_user' | translate }}" />

            @if (walkInUsersLoading()) {
              <div class="absolute right-4 top-1/2 -translate-y-1/2">
                <div class="w-5 h-5 border-2 border-amber-500/20 border-t-amber-500 rounded-full animate-spin"></div>
              </div>
            }
          </div>

          @if (walkInUsers().length > 0 && !selectedWalkInUser()) {
            <div class="mt-2 bg-white/5 rounded-xl border border-white/10 max-h-48 overflow-y-auto">
              @for (user of walkInUsers(); track user.id) {
                <button
                  type="button"
                  data-testid="walkin-points-user-result"
                  (click)="selectWalkInUser(user)"
                  class="w-full p-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between gap-3 border-b border-white/5 last:border-0"
                  [class.opacity-50]="
                    user.pointBalance !== undefined &&
                    user.pointBalance !== null &&
                    user.pointBalance < calculatedTotalPoints()
                  ">
                  <div class="flex items-center gap-3">
                    <span
                      class="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-sm">
                      {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                    </span>
                    <div>
                      <p class="font-medium">{{ user.firstName }} {{ user.lastName }}</p>
                    </div>
                  </div>
                  @if (user.pointBalance !== undefined && user.pointBalance !== null) {
                    <div class="text-right">
                      <p
                        class="text-sm font-bold"
                        [class.text-amber-400]="user.pointBalance >= calculatedTotalPoints()"
                        [class.text-red-400]="user.pointBalance < calculatedTotalPoints()">
                        {{ user.pointBalance }} b.
                      </p>
                      @if (user.pointBalance < calculatedTotalPoints()) {
                        <p class="text-[10px] text-red-400">{{ 'pos.checkout.insufficient' | translate }}</p>
                      }
                    </div>
                  }
                </button>
              }
            </div>
          }

          @if (selectedWalkInUser(); as user) {
            <div
              class="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span
                  class="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
                  {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                </span>
                <div>
                  <p class="font-medium text-white">{{ user.firstName }} {{ user.lastName }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                @if (user.pointBalance !== undefined && user.pointBalance !== null) {
                  <span
                    class="px-2 py-1 rounded-lg text-xs font-bold"
                    [class.text-amber-400]="user.pointBalance >= calculatedTotalPoints()"
                    [class.text-red-400]="user.pointBalance < calculatedTotalPoints()"
                    [ngClass]="{
                      'bg-amber-500/20': user.pointBalance >= calculatedTotalPoints(),
                      'bg-red-500/20': user.pointBalance < calculatedTotalPoints(),
                    }">
                    {{ user.pointBalance }} b.
                  </span>
                }
                <button
                  type="button"
                  (click)="selectedWalkInUser.set(null); walkInUserSearchQuery.set('')"
                  class="p-2 rounded-full hover:bg-white/10 transition-colors">
                  <span class="material-symbols-outlined text-white/50">close</span>
                </button>
              </div>
            </div>
            @if (
              user.pointBalance !== undefined &&
              user.pointBalance !== null &&
              user.pointBalance < calculatedTotalPoints()
            ) {
              <p class="text-xs text-red-400 mt-2 flex items-center gap-1">
                <span class="material-symbols-outlined text-sm">error</span>
                {{ 'pos.checkout.not_enough_points_user' | translate }}
              </p>
            }
          }
        </section>
      }
    }

    @if (checkoutType() === CheckoutType.NEW_TAB) {
      <section>
        <h3 class="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
          <span class="material-symbols-outlined text-base">person</span>
          {{ 'pos.checkout.tab_type' | translate }}
        </h3>
        <div class="flex gap-3 mb-4">
          <button
            type="button"
            data-testid="newtab-type-user"
            (click)="newTabType.set(PosTabType.User)"
            class="flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2"
            [class.border-neon]="newTabType() === PosTabType.User"
            [ngClass]="{
              'bg-neon/10': newTabType() === PosTabType.User,
              'border-white/10': newTabType() !== PosTabType.User,
              'bg-white/5': newTabType() !== PosTabType.User,
            }">
            <span
              class="material-symbols-outlined"
              [class.text-neon]="newTabType() === PosTabType.User"
              >person</span
            >
            <span class="font-medium">{{ 'pos.checkout.user' | translate }}</span>
          </button>

          <button
            type="button"
            data-testid="newtab-type-visitor"
            (click)="newTabType.set(PosTabType.Visitor)"
            class="flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2"
            [class.border-neon]="newTabType() === PosTabType.Visitor"
            [ngClass]="{
              'bg-neon/10': newTabType() === PosTabType.Visitor,
              'border-white/10': newTabType() !== PosTabType.Visitor,
              'bg-white/5': newTabType() !== PosTabType.Visitor,
            }">
            <span
              class="material-symbols-outlined"
              [class.text-neon]="newTabType() === PosTabType.Visitor"
              >person_outline</span
            >
            <span class="font-medium">{{ 'pos.checkout.visitor' | translate }}</span>
          </button>
        </div>

        @if (newTabType() === PosTabType.User) {
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-white/30">search</span>
            <input
              type="text"
              data-testid="newtab-user-search"
              [value]="userSearchQuery()"
              (input)="searchUsers($any($event.target).value)"
              class="w-full bg-transparent border border-white rounded-full py-3 pl-12 pr-4 text-white placeholder-white/70 focus:outline-none focus:border-neon transition-all"
              placeholder="{{ 'pos.checkout.search_user' | translate }}" />

            @if (usersLoading()) {
              <div class="absolute right-4 top-1/2 -translate-y-1/2">
                <div class="w-5 h-5 border-2 border-neon/20 border-t-neon rounded-full animate-spin"></div>
              </div>
            }
          </div>

          @if (users().length > 0 && !selectedUser()) {
            <div
              class="mt-2 bg-white/5 rounded-xl border border-white/10 max-h-48 overflow-y-auto"
              data-testid="newtab-user-results">
              @for (user of users(); track user.id) {
                <button
                  type="button"
                  data-testid="newtab-user-result"
                  (click)="selectUser(user)"
                  class="w-full p-3 text-left hover:bg-white/10 transition-colors flex items-center gap-3 border-b border-white/5 last:border-0">
                  <span
                    class="w-8 h-8 rounded-full bg-neon/20 flex items-center justify-center text-neon font-bold text-sm">
                    {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                  </span>
                  <div>
                    <p class="font-medium">{{ user.firstName }} {{ user.lastName }}</p>
                  </div>
                </button>
              }
            </div>
          }

          @if (selectedUser(); as user) {
            <div
              class="mt-3 rounded-xl p-3 flex items-center justify-between border"
              [class.bg-neon/10]="!selectedUserHasOpenTab()"
              [class.border-neon/30]="!selectedUserHasOpenTab()"
              [class.bg-red-500/10]="selectedUserHasOpenTab()"
              [class.border-red-500/30]="selectedUserHasOpenTab()">
              <div class="flex items-center gap-3">
                <span
                  class="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                  [class.bg-neon/20]="!selectedUserHasOpenTab()"
                  [class.text-neon]="!selectedUserHasOpenTab()"
                  [class.bg-red-500/20]="selectedUserHasOpenTab()"
                  [class.text-red-400]="selectedUserHasOpenTab()">
                  {{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}
                </span>
                <div>
                  <p class="font-medium text-white">{{ user.firstName }} {{ user.lastName }}</p>
                  @if (selectedUserHasOpenTab()) {
                    <p
                      class="text-xs text-red-400"
                      data-testid="newtab-user-has-open-tab">
                      {{ 'pos.checkout.user_has_open_tab' | translate }}
                    </p>
                  }
                </div>
              </div>
              <button
                type="button"
                (click)="selectedUser.set(null); userSearchQuery.set('')"
                class="p-2 rounded-full hover:bg-white/10 transition-colors">
                <span class="material-symbols-outlined text-white/50">close</span>
              </button>
            </div>
          }
        } @else {
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-white/30">badge</span>
            <input
              type="text"
              data-testid="newtab-visitor-alias"
              [value]="visitorAlias()"
              (input)="visitorAlias.set($any($event.target).value)"
              class="w-full bg-transparent border border-white rounded-full py-3 pl-12 pr-4 text-white placeholder-white/70 focus:outline-none focus:border-neon transition-all"
              placeholder="{{ 'pos.checkout.visitor_alias' | translate }}" />
          </div>
        }
      </section>
    }

    @if (checkoutType() === CheckoutType.EXISTING_TAB) {
      <section>
        <h3 class="text-sm font-medium text-white/60 mb-3 flex items-center gap-2">
          <span class="material-symbols-outlined text-base">list</span>
          {{ 'pos.checkout.select_tab' | translate }}
        </h3>

        <div class="relative mb-4">
          <span class="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-white/30">search</span>
          <input
            type="text"
            [value]="tabSearchQuery()"
            (input)="onTabSearch($any($event.target).value)"
            class="w-full bg-transparent border border-white rounded-full py-3 pl-12 pr-4 text-white placeholder-white/70 focus:outline-none focus:border-neon transition-all"
            placeholder="{{ 'pos.checkout.search_tab' | translate }}" />

          @if (tabsLoading()) {
            <div class="absolute right-4 top-1/2 -translate-y-1/2">
              <div class="w-5 h-5 border-2 border-neon/20 border-t-neon rounded-full animate-spin"></div>
            </div>
          }
        </div>

        @if (openTabs().length === 0 && !tabsLoading()) {
          <div class="text-center py-8 text-white/40">
            <span class="material-symbols-outlined text-4xl mb-2">credit_card_off</span>
            <p>{{ 'pos.checkout.no_open_tabs' | translate }}</p>
          </div>
        } @else {
          <div class="space-y-2 max-h-48 overflow-y-auto">
            @for (tab of openTabs(); track tab.id) {
              <button
                type="button"
                data-testid="existingtab-option"
                (click)="selectTab(tab)"
                class="w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 text-left"
                [class.border-neon]="selectedTab()?.id === tab.id"
                [ngClass]="{
                  'bg-neon/10': selectedTab()?.id === tab.id,
                  'border-white/10': selectedTab()?.id !== tab.id,
                  'bg-white/5': selectedTab()?.id !== tab.id,
                }">
                <span
                  class="w-10 h-10 rounded-full flex items-center justify-center"
                  [ngClass]="{
                    'bg-blue-500/20': tab.type === PosTabType.User,
                    'bg-orange-500/20': tab.type === PosTabType.Visitor,
                  }">
                  <span
                    class="material-symbols-outlined"
                    [class.text-blue-400]="tab.type === PosTabType.User"
                    [class.text-orange-400]="tab.type === PosTabType.Visitor">
                    {{ tab.type === PosTabType.User ? 'person' : 'person_outline' }}
                  </span>
                </span>
                <div class="flex-1">
                  <p class="font-medium">{{ getTabDisplayName(tab) }}</p>
                  <p class="text-xs text-white/50">{{ tab.createdAt | date: 'short' }}</p>
                </div>
                @if (selectedTab()?.id === tab.id) {
                  <span class="material-symbols-outlined text-neon">check_circle</span>
                }
              </button>
            }
          </div>
        }
      </section>
    }
  </div>

  <div class="p-4 lg:p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
    <button
      type="button"
      data-testid="checkout-cancel"
      (click)="close()"
      class="w-full sm:w-auto sm:flex-1 min-w-0 whitespace-nowrap py-3 px-6 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
      <span class="material-symbols-outlined">arrow_back</span>
      {{ 'pos.checkout.cancel' | translate }}
    </button>

    <button
      type="button"
      data-testid="checkout-confirm"
      (click)="onSubmit()"
      [disabled]="!canSubmit() || submitting()"
      class="w-full sm:w-auto sm:flex-1 min-w-0 whitespace-nowrap py-3 px-6 rounded-full bg-neon text-night font-bold hover:bg-neon/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 uppercase">
      @if (submitting()) {
        <div class="w-5 h-5 border-2 border-night/20 border-t-night rounded-full animate-spin"></div>
      } @else {
        <span class="material-symbols-outlined">check</span>
      }
      @switch (checkoutType()) {
        @case (CheckoutType.WALK_IN) {
          {{ 'pos.checkout.confirm_payment' | translate }}
        }
        @case (CheckoutType.NEW_TAB) {
          {{ 'pos.checkout.open_and_add' | translate }}
        }
        @case (CheckoutType.EXISTING_TAB) {
          {{ 'pos.checkout.add_to_tab' | translate }}
        }
        @case (CheckoutType.OVERHEAD) {
          {{ 'pos.checkout.confirm_overhead' | translate }}
        }
      }
    </button>
  </div>
</div>
